export type AudiencePayload = {
  kind: "event" | "family" | "contact";
  name: string;
  email: string;
  phone?: string;
  city?: string;
  instagramId?: string;
  discoverySource?: string;
  referrerContact?: string;
  referralToken?: string;
  message?: string;
  eventSlug?: string;
  eventName?: string;
  eventDate?: string;
  guestCount?: number;
  musicPreference?: string;
  marketingOptIn?: boolean;
  language?: "de" | "en";
  sourcePage?: string;
  privacyVersion?: string;
  turnstileToken: string;
};

export async function submitAudience(payload: AudiencePayload) {
  const response = await fetch("/api/audience/submit", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });

  const result = (await response.json()) as {
    ok?: boolean;
    error?: string;
    submissionId?: string;
    requiresEmailVerification?: boolean;
  };
  if (!response.ok || !result.ok) {
    throw new Error(result.error || "Submission failed. Please try again.");
  }
  return result;
}

export async function createReferralLink(email: string, eventSlug: string) {
  let lastError = "Referral link creation failed";
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const response = await fetch("/api/referrals/create", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, eventSlug }),
      });
      const result = (await response.json()) as { ok?: boolean; url?: string; error?: string };
      if (response.ok && result.ok && result.url) return result.url;
      lastError = result.error || lastError;
      if (response.status < 500) break;
    } catch (error) {
      lastError = error instanceof Error ? error.message : lastError;
    }
    await new Promise((resolve) => window.setTimeout(resolve, 350));
  }
  throw new Error(lastError);
}

export async function inspectReferralToken(token: string) {
  const response = await fetch(`/api/referrals/inspect?token=${encodeURIComponent(token)}`, { headers: { accept: "application/json" } });
  const result = (await response.json()) as { ok?: boolean; eventSlug?: string; maskedContact?: string };
  if (!response.ok || !result.ok || !result.eventSlug) return null;
  return result;
}

export type StaffTicket = {
  id: string;
  submissionId: string;
  eventSlug: string;
  entryCode: string;
  status: "issued" | "checked_in" | "revoked";
  emailStatus: "pending" | "sending" | "sent" | "failed";
  name: string;
  email: string;
  eventName: string;
  eventDate: string;
  checkedInAt: string | null;
  partySize: number;
  checkedInCount: number;
  remainingEntries: number;
  lastCheckedInAt: string | null;
  lastCheckedInBy: string | null;
  admissionPrice: number;
};

export async function lookupStaffTicket(pin: string, input: { token?: string; query?: string }) {
  const response = await fetch("/api/staff/tickets/lookup", {
    method: "POST",
    headers: { "content-type": "application/json", "X-AXON-Staff-Pin": pin },
    body: JSON.stringify(input),
  });
  const result = await response.json() as { ok?: boolean; ticket?: StaffTicket; error?: string };
  if (!response.ok || !result.ok || !result.ticket) throw new Error(result.error || "Ticket konnte nicht gefunden werden.");
  return result.ticket;
}

export async function checkInStaffTicket(pin: string, ticketId: string, enteringCount = 1, staffLabel = "door") {
  const response = await fetch("/api/staff/tickets/check-in", {
    method: "POST",
    headers: { "content-type": "application/json", "X-AXON-Staff-Pin": pin },
    body: JSON.stringify({ ticketId, enteringCount, staffLabel }),
  });
  const result = await response.json() as { ok?: boolean; status?: StaffTicket["status"]; checkedInAt?: string; checkedInCount?: number; remainingEntries?: number; lastCheckedInAt?: string; error?: string };
  if (!response.ok || !result.ok) throw new Error(result.error || "Check-in fehlgeschlagen.");
  return result;
}
